import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, Volume2, VolumeX, Mic, Square, Axe, Terminal, BookOpen } from "lucide-react";

const SUGGESTIONS = [
  "Explain long division like I'm in 4th grade",
  "Help me understand photosynthesis",
  "Give me a study plan for Algebra I",
  "What's a good hands-on science project?",
];

export default function Tutor() {
  const location = useLocation();
  const lessonCtx = location.state || {};
  const hasCtx = !!lessonCtx.lessonTitle;
  const suggestions = hasCtx
    ? [
        `Explain "${lessonCtx.lessonTitle}" in a simple way`,
        `Give me a fun example about ${lessonCtx.lessonTitle}`,
        `Quiz me on ${lessonCtx.lessonTitle}`,
        `Why does ${lessonCtx.lessonTitle} matter?`,
      ]
    : SUGGESTIONS;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [recording, setRecording] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const voiceModeRef = useRef(false);
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/tutor/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: content, session_id: sessionId, course_title: lessonCtx.courseTitle, lesson_title: lessonCtx.lessonTitle }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.session_id) setSessionId(evt.session_id);
            if (evt.delta) {
              acc += evt.delta;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.delta };
                return copy;
              });
            }
            if (evt.error) toast.error(evt.error);
          } catch {}
        }
      }
      if (voiceModeRef.current && acc.trim()) speak(acc, "auto");
    } catch {
      toast.error("Tutor connection failed. Try again.");
      setMessages((m) => {
        const copy = [...m];
        if (copy[copy.length - 1]?.role === "assistant" && !copy[copy.length - 1].content) copy.pop();
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const speak = async (text, idx) => {
    setTtsBusy(idx);
    try {
      const res = await api.post("/tutor/tts", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play();
    } catch {
      toast.error("Text-to-speech unavailable.");
    } finally {
      setTtsBusy(null);
    }
  };

  const toggleRecord = async () => {
    if (recording) {
      recorderRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        toast.loading("Transcribing…", { id: "stt" });
        try {
          const { data } = await api.post("/tutor/transcribe", fd);
          toast.success("Transcribed", { id: "stt" });
          if (voiceModeRef.current && data.text?.trim()) send(data.text);
          else setInput((v) => (v ? v + " " : "") + data.text);
        } catch {
          toast.error("Transcription failed.", { id: "stt" });
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  return (
    <div className="kid-theme">
    <div className="mx-auto flex h-[calc(100vh-73px)] max-w-4xl flex-col px-5 lg:px-8">
      <div className="flex items-center gap-3 border-b-2 border-border py-5">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Axe className="h-6 w-6" strokeWidth={2.5} /></span>
        <div className="flex-1">
          <h1 className="font-head text-2xl font-black leading-none">Hornsby AI Tutor</h1>
          {hasCtx ? (
            <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-extrabold text-primary" data-testid="tutor-lesson-context">
              <BookOpen className="h-3.5 w-3.5" strokeWidth={2.75} /> Helping with: {lessonCtx.lessonTitle}
            </p>
          ) : (
            <p className="mt-1 text-sm font-semibold text-muted-foreground">Patient, honest, and always ready. Powered by GPT.</p>
          )}
        </div>
        <button
          onClick={() => { setVoiceMode((v) => !v); if (audioRef.current) audioRef.current.pause(); }}
          data-testid="tutor-voice-toggle"
          className={`flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-extrabold transition-colors ${voiceMode ? "border-primary bg-primary text-white" : "border-border bg-card text-muted-foreground hover:border-primary"}`}
          title="Voice Mode: I read my answers out loud and you can talk back"
        >
          {voiceMode ? <Volume2 className="h-5 w-5" strokeWidth={2.5} /> : <VolumeX className="h-5 w-5" strokeWidth={2.5} />} Voice {voiceMode ? "On" : "Off"}
        </button>
      </div>

      {voiceMode && (
        <div className="mt-3 flex items-center gap-2 rounded-2xl border-2 border-sky-200 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-700" data-testid="voice-mode-banner">
          <Volume2 className="h-5 w-5 text-sky-500" strokeWidth={2.5} /> Voice Mode is on — I'll read my answers aloud. Tap the mic to talk to me!
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-6 overflow-y-auto py-8" data-testid="tutor-messages">
        {messages.length === 0 && (
          <div className="mx-auto max-w-lg text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-accent text-secondary">{hasCtx ? <BookOpen className="h-10 w-10" strokeWidth={2.25} /> : <Terminal className="h-10 w-10" strokeWidth={2.25} />}</div>
            <h2 className="mt-5 font-head text-3xl font-black">{hasCtx ? `Let's work on this lesson!` : "What are we working on?"}</h2>
            <p className="mt-2 text-base font-semibold text-muted-foreground">{hasCtx ? `${lessonCtx.lessonTitle}${lessonCtx.courseTitle ? ` · ${lessonCtx.courseTitle}` : ""} — ask me anything about it.` : "Ask about any subject. I'll walk you through it step by step."}</p>
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="tutor-suggestion" className="rounded-2xl border-2 border-border bg-card p-4 text-left text-sm font-bold shadow-sm transition-all hover:-translate-y-1 hover:border-primary hover:text-primary">{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-3xl border-2 p-5 shadow-sm ${m.role === "user" ? "rounded-tr-md border-primary/30 bg-primary/10" : "rounded-tl-md border-border bg-card"}`} data-testid={`tutor-msg-${m.role}`}>
              {m.role === "assistant" && (
                <div className="mb-2 flex items-center justify-between gap-4">
                  <span className="flex items-center gap-2 text-sm font-extrabold text-primary"><span className="h-2.5 w-2.5 rounded-full bg-primary" /> Tutor</span>
                  {m.content && (
                    <button onClick={() => speak(m.content, i)} className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-500 text-white transition-transform hover:scale-110" data-testid="tutor-tts-btn" title="Read aloud">
                      {ttsBusy === i ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" strokeWidth={2.5} />}
                    </button>
                  )}
                </div>
              )}
              <div className="whitespace-pre-wrap text-base font-semibold leading-relaxed">
                {m.content}
                {m.role === "assistant" && !m.content && streaming && <span className="inline-block h-4 w-2 animate-blink bg-primary align-middle" />}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <div className="border-t-2 border-border py-4">
        <div className="flex items-end gap-2 rounded-3xl border-2 border-border bg-card p-2 focus-within:border-primary">
          <button onClick={toggleRecord} data-testid="tutor-mic-btn" className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors ${recording ? "bg-red-500 text-white animate-pulse" : "bg-muted text-muted-foreground hover:bg-primary hover:text-white"}`} title={recording ? "Stop" : "Voice input"}>
            {recording ? <Square className="h-5 w-5" strokeWidth={2.5} /> : <Mic className="h-5 w-5" strokeWidth={2.5} />}
          </button>
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask the tutor anything…"
            data-testid="tutor-input"
            rows={1}
            className="min-h-[44px] resize-none border-0 bg-transparent text-base font-semibold focus-visible:ring-0"
          />
          <Button onClick={() => send()} disabled={streaming || !input.trim()} className="h-11 w-11 shrink-0 rounded-full bg-primary p-0 tactile hover:bg-primary" data-testid="tutor-send-btn">
            {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" strokeWidth={2.5} />}
          </Button>
        </div>
        <p className="mt-2 text-center text-xs font-semibold text-muted-foreground">The tutor guides — it won't hand over graded answers.</p>
      </div>
    </div>
    </div>
  );
}
